// A BattlePlan is identified by (kind, periodKey) — "2026-07-12" for a daily plan,
// "2026-W28" for a weekly one. These build and step those keys.
//
// The weekly key is ISO-8601: weeks start on Monday, and week 1 is the one holding
// the year's first Thursday. That rule is why a date in early January can legitimately
// belong to week 52 of the previous year — hence the calculation rather than a
// simple divide-by-seven.

export type Period = "daily" | "weekly";

const DAY_MS = 86_400_000;

/** The key for the period a date falls in. */
export function periodKeyFor(kind: Period, date: Date): string {
  return kind === "daily" ? dayKey(date) : isoWeekKey(date);
}

/** Step a period forward (+1) or back (-1). */
export function shiftPeriod(kind: Period, date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta * (kind === "daily" ? 1 : 7));
  return next;
}

/** How the period reads to a person, e.g. "Sun, 12 Jul 2026" or "Week 28 · 6–12 Jul". */
export function periodLabel(kind: Period, date: Date): string {
  if (kind === "daily") {
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  const monday = startOfIsoWeek(date);
  const sunday = new Date(monday.getTime() + 6 * DAY_MS);
  return `${isoWeekKey(date)} · ${dayMonth(monday)} – ${dayMonth(sunday)}`;
}

function dayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** ISO-8601 week key, e.g. "2026-W28". */
function isoWeekKey(date: Date): string {
  const thursday = startOfIsoWeek(date);
  thursday.setDate(thursday.getDate() + 3); // the week's Thursday decides its year

  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  const firstWeekMonday = startOfIsoWeek(firstThursday);
  const week = Math.round((thursday.getTime() - firstWeekMonday.getTime()) / (7 * DAY_MS)) + 1;

  return `${thursday.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Weeks start on Monday. */
function startOfIsoWeek(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
}

function dayMonth(date: Date): string {
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
